import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Shield } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 py-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Link to="/register">
            <Button variant="ghost" size="icon" data-testid="back-to-register-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-[#547d91]" />
            <h1 className="font-heading text-2xl font-bold text-slate-900">Privacy Policy</h1>
          </div>
        </div>

        <Card className="shadow-lg border-0">
          <CardContent className="p-6 md:p-8 prose prose-slate max-w-none">
            <h2 className="text-xl font-semibold text-[#547d91] mt-0">Investeps Property App</h2>
            <p className="text-sm text-slate-500">Effective Date: 19 February 2026</p>

            <h3>1. Introduction</h3>
            <p>
              This Privacy Policy explains how Investeps Property ("Investeps Property", "we", "us", or "our") 
              collects, uses, discloses and protects your personal information when you use the Investeps Property 
              mobile application ("App").
            </p>
            <p>The App is created, owned and operated by Investeps Property.</p>
            <p>
              We are committed to protecting your privacy and handling your personal information in accordance 
              with the Privacy Act 1988 (Cth) and the Australian Privacy Principles.
            </p>
            <p>
              By using the App, you consent to the collection and use of your information as described in this 
              Privacy Policy.
            </p>

            <h3>2. Information We Collect</h3>
            <p>We may collect the following types of personal information:</p>
            <ul>
              <li>Your full name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Property preferences or enquiry details</li>
              <li>Device information (such as device type, operating system, app usage data)</li>
              <li>Any other information you provide through the App</li>
            </ul>
            <p>We only collect information that is reasonably necessary for our business functions and activities.</p>

            <h3>3. How We Use Your Information</h3>
            <p>We may use your personal information to:</p>
            <ul>
              <li>Contact you regarding property enquiries or services</li>
              <li>Provide updates about properties, listings or opportunities</li>
              <li>Respond to your questions or requests</li>
              <li>Improve the functionality and performance of the App</li>
              <li>Comply with legal obligations</li>
            </ul>
            <p>
              By using the App, you acknowledge and agree that you may be contacted by Investeps Property via 
              phone, SMS, email, push notification, or other electronic communication methods in relation to 
              your enquiry, property interests, or relevant services.
            </p>

            <h3>4. Marketing Communications</h3>
            <p>
              We may send you marketing communications about properties, services or promotions that may be 
              of interest to you.
            </p>
            <p>You may opt out of marketing communications at any time by:</p>
            <ul>
              <li>Clicking the unsubscribe link in emails</li>
              <li>Replying STOP to SMS (where applicable)</li>
              <li>Adjusting your notification preferences within the App (where available)</li>
              <li>Contacting us directly</li>
            </ul>

            <h3>5. Disclosure of Information</h3>
            <p>We may disclose your personal information to:</p>
            <ul>
              <li>Employees, contractors or service providers who assist us in operating the App</li>
              <li>IT and cloud storage providers</li>
              <li>Professional advisers (e.g., legal or accounting)</li>
              <li>Government or regulatory authorities where required by law</li>
            </ul>
            <p>We do not sell your personal information to third parties.</p>

            <h3>6. Data Storage and Security</h3>
            <p>
              We take reasonable steps to protect your personal information from misuse, interference, loss, 
              unauthorised access, modification or disclosure.
            </p>
            <p>
              Information may be stored electronically using secure third-party service providers, which may 
              be located in Australia or overseas.
            </p>

            <h3>7. Access and Correction</h3>
            <p>
              You may request access to or correction of your personal information by contacting us using the 
              details below. We will respond within a reasonable timeframe in accordance with applicable privacy laws.
            </p>

            <h3>8. Cookies and Analytics</h3>
            <p>
              The App may use cookies, analytics tools or similar technologies to collect information about 
              usage and improve user experience.
            </p>
            <p>You may control certain tracking settings through your device settings.</p>

            <h3>9. Changes to This Policy</h3>
            <p>
              We may update this Privacy Policy from time to time. The latest version will always be available 
              within the App. Continued use of the App constitutes acceptance of any updates.
            </p>

            <h3>10. Contact Us</h3>
            <p>
              If you have any questions about this Privacy Policy or how we handle your personal information, 
              please contact:
            </p>
            <address className="not-italic bg-slate-50 p-4 rounded-lg">
              <strong>Investeps Property</strong><br />
              844 Old Cleveland Rd<br />
              Carina QLD 4152<br />
              Email: <a href="mailto:info@investepsproperty.com" className="text-[#547d91]">info@investepsproperty.com</a>
            </address>
          </CardContent>
        </Card>

        {/* Back to Register Button */}
        <div className="mt-6 text-center">
          <Link to="/register">
            <Button className="bg-[#547d91] hover:bg-[#456a7a]" data-testid="return-to-register-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Return to Registration
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
